import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Mail, Users, MessageSquare, Calendar, MapPin, Phone, Globe, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const Admin = () => {
  const [emailSubscriptions, setEmailSubscriptions] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('emails');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [emailsResponse, contactsResponse] = await Promise.all([
        fetch('/api/emails/subscriptions'),
        fetch('/api/contact/submissions')
      ]);

      if (emailsResponse.ok) {
        const emails = await emailsResponse.json();
        setEmailSubscriptions(emails);
      }

      if (contactsResponse.ok) {
        const contacts = await contactsResponse.json();
        setContactSubmissions(contacts);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (submissionId, newStatus) => {
    try {
      const response = await fetch(`/api/contact/submissions/${submissionId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        // Update local state
        setContactSubmissions(prev => 
          prev.map(submission => 
            submission.id === submissionId 
              ? { ...submission, status: newStatus }
              : submission
          )
        );
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'converted': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-brand-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#D4623C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-brand-charcoal-600">Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-brand-ivory-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-brand-charcoal mb-2">Admin Dashboard</h1>
          <p className="text-brand-charcoal-600">Manage email subscriptions and contact form submissions</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-8 bg-white p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('emails')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'emails' 
                ? 'bg-[#D4623C] text-white' 
                : 'text-brand-charcoal-600 hover:text-brand-charcoal'
            }`}
          >
            <Mail className="inline w-4 h-4 mr-2" />
            Email Subscriptions ({emailSubscriptions.length})
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeTab === 'contacts' 
                ? 'bg-[#D4623C] text-white' 
                : 'text-brand-charcoal-600 hover:text-brand-charcoal'
            }`}
          >
            <MessageSquare className="inline w-4 h-4 mr-2" />
            Contact Submissions ({contactSubmissions.length})
          </button>
        </div>

        {/* Email Subscriptions Tab */}
        {activeTab === 'emails' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {emailSubscriptions.map((subscription) => (
                <Card key={subscription.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-[#D4623C] rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-brand-charcoal">{subscription.email}</p>
                          <p className="text-sm text-brand-charcoal-600 capitalize">{subscription.source}</p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">
                        Active
                      </Badge>
                    </div>
                    <div className="space-y-2 text-sm text-brand-charcoal-600">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>Subscribed: {new Date(subscription.subscribed_at).toLocaleDateString()}</span>
                      </div>
                      {subscription.metadata && Object.keys(subscription.metadata).length > 0 && (
                        <div className="mt-3 p-2 bg-brand-ivory-50 rounded">
                          <p className="text-xs font-medium text-brand-charcoal mb-1">Additional Info:</p>
                          {Object.entries(subscription.metadata).map(([key, value]) => (
                            <p key={key} className="text-xs text-brand-charcoal-600">
                              {key}: {value}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {emailSubscriptions.length === 0 && (
              <div className="text-center py-12">
                <Mail className="w-12 h-12 text-brand-charcoal-300 mx-auto mb-4" />
                <p className="text-brand-charcoal-600">No email subscriptions yet</p>
              </div>
            )}
          </div>
        )}

        {/* Contact Submissions Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-6">
            {contactSubmissions.map((submission) => (
              <Card key={submission.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl text-brand-charcoal">{submission.full_name}</CardTitle>
                      <CardDescription className="text-brand-charcoal-600">
                        {submission.restaurant_name} • {submission.city}, {submission.state}
                      </CardDescription>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getStatusColor(submission.status)}>
                        {submission.status}
                      </Badge>
                      <div className="flex space-x-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateSubmissionStatus(submission.id, 'contacted')}
                          className="text-xs"
                        >
                          Contacted
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateSubmissionStatus(submission.id, 'converted')}
                          className="text-xs"
                        >
                          Converted
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-brand-charcoal-600" />
                        <span className="text-sm text-brand-charcoal-600">{submission.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-brand-charcoal-600" />
                        <span className="text-sm text-brand-charcoal-600">{submission.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-brand-charcoal-600" />
                        <span className="text-sm text-brand-charcoal-600">{submission.country}</span>
                      </div>
                      {submission.website && (
                        <div className="flex items-center space-x-2">
                          <Globe className="w-4 h-4 text-brand-charcoal-600" />
                          <a href={submission.website} target="_blank" rel="noopener noreferrer" className="text-sm text-[#D4623C] hover:underline">
                            {submission.website}
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {submission.challenge && (
                        <div>
                          <p className="text-sm font-medium text-brand-charcoal mb-1">Challenge:</p>
                          <p className="text-sm text-brand-charcoal-600">{submission.challenge}</p>
                        </div>
                      )}
                      {submission.review_count && (
                        <div>
                          <p className="text-sm font-medium text-brand-charcoal mb-1">Monthly Reviews:</p>
                          <p className="text-sm text-brand-charcoal-600">{submission.review_count}</p>
                        </div>
                      )}
                      {submission.weekly_reservations && (
                        <div>
                          <p className="text-sm font-medium text-brand-charcoal mb-1">Weekly Reservations:</p>
                          <p className="text-sm text-brand-charcoal-600">{submission.weekly_reservations}</p>
                        </div>
                      )}
                      {submission.hear_about && (
                        <div>
                          <p className="text-sm font-medium text-brand-charcoal mb-1">How they heard about us:</p>
                          <p className="text-sm text-brand-charcoal-600">{submission.hear_about}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-brand-charcoal-200">
                    <div className="flex items-center space-x-2 text-sm text-brand-charcoal-600">
                      <Calendar className="w-4 h-4" />
                      <span>Submitted: {new Date(submission.submitted_at).toLocaleString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {contactSubmissions.length === 0 && (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-brand-charcoal-300 mx-auto mb-4" />
                <p className="text-brand-charcoal-600">No contact form submissions yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;

